import type { RouterOutput } from "@/lib/trpc-client";

/**
 * Staff can pause a program's new submissions without switching the program
 * off. Creators keep the portal, their videos, payouts and messages; only a
 * new upload is held. The server is the real check (the upload, check and
 * confirm calls refuse with CONFLICT); these decide what the portal shows
 * before a creator gets that far, and what the staff setting sends.
 */

/** A pause as the portal payload carries it. */
export type SubmissionsPause = NonNullable<
  RouterOutput["creatorPortal"]["getPortalHome"]["program"]["submissionsPaused"]
>;

/**
 * The pause in force, or null while the program takes new videos.
 *
 * A payload without the field, from a backend deployed before it, reads as
 * open: that backend has no pause to enforce.
 */
export function activeSubmissionsPause(
  program: { submissionsPaused?: SubmissionsPause | null } | null | undefined,
): SubmissionsPause | null {
  return program?.submissionsPaused ?? null;
}

/** The staff note as creators see it: trimmed, or null when there is none. */
export function submissionsPausedNote(
  pause: Pick<SubmissionsPause, "note">,
): string | null {
  return pause.note?.trim() || null;
}

/** What the staff note field sends: the trimmed draft, or null to clear it. */
export function pausedNoteToSave(draft: string): string | null {
  return draft.trim() || null;
}

/**
 * Whether the draft would change the saved note. Whitespace alone is not a
 * change, since the server trims it away.
 */
export function pausedNoteChanged(
  draft: string,
  saved: string | null,
): boolean {
  return pausedNoteToSave(draft) !== (saved?.trim() || null);
}
